/** Delete confirm dialog с выбором стратегии для детей и товаров.
 *
 * Backend: DELETE /admin/v1/categories/{id}?children_strategy=...&products_strategy=...
 *
 * Стратегии:
 *   children: block | parent | move | cascade
 *   products: block | parent | move | detach
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";

interface CategoryRaw {
  id: number;
  parent_id: number | null;
  slug: string;
  products_count?: number;
  translations: Array<{ language: string; name: string }>;
}

interface Props {
  open: boolean;
  onClose: () => void;
  category: {
    id: number;
    name: string;
    parent_id: number | null;
    products_count: number;
  };
  /** Сколько прямых детей у категории (best-effort из tree сверху). */
  childrenCount: number;
  onDeleted?: () => void;
}

type ChildrenStrategy = "parent" | "move" | "cascade";
type ProductsStrategy = "parent" | "move" | "detach";

export function CategoryDeleteDialog({
  open,
  onClose,
  category,
  childrenCount,
  onDeleted,
}: Props): JSX.Element {
  const hasChildren = childrenCount > 0;
  const hasProducts = category.products_count > 0;
  const hasParent = category.parent_id != null;

  const [childrenStrategy, setChildrenStrategy] = useState<ChildrenStrategy>(
    hasParent ? "parent" : "cascade",
  );
  const [productsStrategy, setProductsStrategy] = useState<ProductsStrategy>(
    hasParent ? "parent" : "detach",
  );
  const [moveChildrenTo, setMoveChildrenTo] = useState<number | null>(null);
  const [moveProductsTo, setMoveProductsTo] = useState<number | null>(null);

  // Reset values when dialog re-opens
  useEffect(() => {
    if (!open) return;
    setChildrenStrategy(hasParent ? "parent" : "cascade");
    setProductsStrategy(hasParent ? "parent" : "detach");
    setMoveChildrenTo(null);
    setMoveProductsTo(null);
  }, [open, hasParent]);

  const { data: allCategories } = useQuery({
    queryKey: ["admin-categories-flat-delete"],
    queryFn: async () => {
      const { data } = await api.get<CategoryRaw[]>("/categories");
      return data;
    },
    enabled: open,
  });

  const flatOptions =
    allCategories
      ?.filter((c) => c.id !== category.id)
      .map((c) => {
        const ru = c.translations.find((t) => t.language === "ru");
        return { id: c.id, label: ru?.name ?? c.slug };
      })
      .sort((a, b) => a.label.localeCompare(b.label, "ru")) ?? [];

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const params = new URLSearchParams();
      const cs = hasChildren ? childrenStrategy : "block";
      const ps = hasProducts ? productsStrategy : "block";
      params.set("children_strategy", cs);
      params.set("products_strategy", ps);
      if (cs === "move" && moveChildrenTo != null) {
        params.set("move_children_to", String(moveChildrenTo));
      }
      if (ps === "move" && moveProductsTo != null) {
        params.set("move_products_to", String(moveProductsTo));
      }
      await api.delete(`/categories/${category.id}?${params}`);
    },
    onSuccess: () => {
      toast.success(`Категория «${category.name}» удалена`);
      onClose();
      onDeleted?.();
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.error?.message ?? "Не удалось удалить");
    },
  });

  const canSubmit =
    (!hasChildren || childrenStrategy !== "move" || moveChildrenTo != null) &&
    (!hasProducts || productsStrategy !== "move" || moveProductsTo != null);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            Удалить «{category.name}»
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {!hasChildren && !hasProducts && (
            <p className="text-muted-foreground">
              Категория пустая — никаких лишних действий. Подтвердите удаление.
            </p>
          )}

          {hasChildren && (
            <section className="space-y-2 border rounded p-3">
              <Label className="text-xs">
                Подкатегории: <strong>{childrenCount}</strong>
              </Label>
              <Select
                value={childrenStrategy}
                onValueChange={(v: ChildrenStrategy) => setChildrenStrategy(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {hasParent && (
                    <SelectItem value="parent">
                      Перевесить на родительскую категорию
                    </SelectItem>
                  )}
                  <SelectItem value="move">
                    Переместить в другую категорию…
                  </SelectItem>
                  <SelectItem value="cascade">
                    Удалить вместе со всем поддеревом (рекурсивно)
                  </SelectItem>
                </SelectContent>
              </Select>
              {childrenStrategy === "move" && (
                <Select
                  value={moveChildrenTo ? String(moveChildrenTo) : ""}
                  onValueChange={(v) => setMoveChildrenTo(Number(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите целевую категорию" />
                  </SelectTrigger>
                  <SelectContent>
                    {flatOptions.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </section>
          )}

          {hasProducts && (
            <section className="space-y-2 border rounded p-3">
              <Label className="text-xs">
                Товары в категории: <strong>{category.products_count}</strong>
              </Label>
              <Select
                value={productsStrategy}
                onValueChange={(v: ProductsStrategy) => setProductsStrategy(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {hasParent && (
                    <SelectItem value="parent">
                      Перевесить на родительскую категорию
                    </SelectItem>
                  )}
                  <SelectItem value="move">
                    Переместить в другую категорию…
                  </SelectItem>
                  <SelectItem value="detach">
                    Отвязать (товары останутся без primary категории)
                  </SelectItem>
                </SelectContent>
              </Select>
              {productsStrategy === "move" && (
                <Select
                  value={moveProductsTo ? String(moveProductsTo) : ""}
                  onValueChange={(v) => setMoveProductsTo(Number(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите целевую категорию" />
                  </SelectTrigger>
                  <SelectContent>
                    {flatOptions.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </section>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="destructive"
            onClick={() => deleteMutation.mutate()}
            disabled={!canSubmit || deleteMutation.isPending}
          >
            {deleteMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <Trash2 className="w-3.5 h-3.5 mr-1" />
            )}
            Удалить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
